import { useState, useEffect, useMemo } from "react";

const VENDORS = ["PFG", "Specialty Foods", "KUNA"];
const VCOLOR = { "PFG": "#C04A1A", "Specialty Foods": "#1A6640", "KUNA": "#1A3A6B" };
const VLIGHT = { "PFG": "#FDF0EB", "Specialty Foods": "#EBF5F0", "KUNA": "#EBF0F8" };
const CATEGORIES = ["Proteins","Produce","Dairy","Dry Goods","Oils & Sauces","Beverages","Tortillas/Bread","Other"];

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

function getWeekKey(date = new Date()) {
  const d = new Date(date); d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const y = new Date(d.getFullYear(), 0, 1);
  const w = Math.ceil((((d - y) / 86400000) + 1) / 7);
  return `${d.getFullYear()}-W${String(w).padStart(2,"0")}`;
}

function weekLabel(key) {
  const [year, w] = key.split("-W");
  const jan1 = new Date(parseInt(year), 0, 1);
  const mon = new Date(jan1.getTime() + (parseInt(w)-1)*7*86400000);
  mon.setDate(mon.getDate() - mon.getDay() + 1);
  return mon.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
}

function norm(str) {
  return str.toLowerCase()
    .replace(/boneless|skinless|fresh|frozen|sliced|diced|whole|raw|cooked|organic|large|small|medium|extra|jumbo|fancy|choice|select|grade/g,"")
    .replace(/[^a-z0-9 ]/g,"").replace(/\s+/g," ").trim();
}

function catGuess(n) {
  n = n.toLowerCase();
  if (/chicken|beef|pork|shrimp|fish|chorizo|steak|ground|meat|tilapia|tuna|skirt|brisket|ribeye|tongue|tripe|arrachera/.test(n)) return "Proteins";
  if (/tomato|onion|pepper|lime|lemon|avocado|cilantro|lettuce|jalape|garlic|produce|mushroom|potato|tomatillo|squash|zucchini/.test(n)) return "Produce";
  if (/cheese|cream|milk|sour|butter|dairy|queso|egg/.test(n)) return "Dairy";
  if (/rice|bean|flour|sugar|salt|corn|masa|spice|seasoning|oregano|cumin|paprika|chili|bay/.test(n)) return "Dry Goods";
  if (/oil|sauce|salsa|vinegar|mayo|dressing|ketchup/.test(n)) return "Oils & Sauces";
  if (/soda|juice|water|beer|drink|tea|coffee|syrup|horchata|jamaica/.test(n)) return "Beverages";
  if (/tortilla|bread|roll|bun|chip|tostada/.test(n)) return "Tortillas/Bread";
  return "Other";
}

// fetch with a hard timeout. Without this, a slow/large PDF parse or a
// stalled upstream response leaves the browser's fetch() pending forever —
// no error, no log entry, nothing the UI can react to. This forces it to
// fail with a clear message after timeoutMs so the user gets real feedback
// instead of an endless spinner they have to close-and-reopen the tab to escape.
async function fetchWithTimeout(url, options, timeoutMs = 240000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return res;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error("Request timed out after " + Math.round(timeoutMs / 1000) + "s. The file may be large or the server may be slow — try again.");
    }
    throw err;
  }
}

async function parsePDFAutoDetect(b64, filename, attempt = 0) {
  let res;
  try {
    res = await fetchWithTimeout("/api/parse-pdf", {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ b64, filename })
    }, 100000);
  } catch (err) {
    if (attempt < 2) {
      await new Promise(r => setTimeout(r, 3000));
      return parsePDFAutoDetect(b64, filename, attempt + 1);
    }
    throw err;
  }
  if (!res.ok) {
    if (attempt < 2) {
      await new Promise(r => setTimeout(r, 3000));
      return parsePDFAutoDetect(b64, filename, attempt + 1);
    }
    throw new Error("PDF parse failed after retries");
  }
  const parsed = await res.json();
  return {
    vendor: parsed.vendor || "Unknown",
    items: (parsed.items || []).map(i => ({...i, category: catGuess(i.name)}))
  };
}

async function parseOrderGuide(b64) {
  const res = await fetchWithTimeout("/api/parse-order-guide", {
    method:"POST", headers:{"Content-Type":"application/json"},
    body: JSON.stringify({ b64 })
  }, 100000);
  const items = await res.json();
  if (!res.ok) throw new Error(items.error || "API error");
  return items.map(i => ({...i, category: catGuess(i.name)}));
}

function matchVendors(vdata) {
  const map = {};
  Object.keys(vdata).forEach(v => {
    (vdata[v]||[]).forEach(item => {
      const k = norm(item.name);
      if (!map[k]) map[k] = {name: item.name, category: item.category, vendors:{}};
      map[k].vendors[v] = {price: item.price, unit: item.unit};
      if (item.name.length > map[k].name.length) map[k].name = item.name;
    });
  });
  return Object.values(map);
}

function matchOrderToVendors(orderItems, vendorData) {
  return orderItems.map(orderItem => {
    const ok = norm(orderItem.name);
    const matches = {};
    Object.keys(vendorData).forEach(v => {
      const vItems = vendorData[v] || [];
      let best = null; let bestScore = 0;
      vItems.forEach(vi => {
        const vk = norm(vi.name);
        const okWords = ok.split(" ").filter(w => w.length > 2);
        const vkWords = vk.split(" ").filter(w => w.length > 2);
        const shared = okWords.filter(w => vkWords.includes(w)).length;
        const score = shared / Math.max(okWords.length, 1);
        if (score > bestScore && score >= 0.35) { bestScore = score; best = vi; }
      });
      if (best) matches[v] = {price: best.price, unit: best.unit, matchedName: best.name, score: bestScore};
    });
    const entries = Object.entries(matches).filter(([,v])=>v?.price>0).map(([vendor,v])=>({vendor,...v}));
    const bestEntry = entries.length ? entries.reduce((a,b)=>a.price<b.price?a:b) : null;
    const worstEntry = entries.length ? entries.reduce((a,b)=>a.price>b.price?a:b) : null;
    return {...orderItem, matches, entries, best: bestEntry, worst: worstEntry, savings: (worstEntry && bestEntry) ? worstEntry.price - bestEntry.price : 0, priceCount: entries.length};
  });
}

export default function App() {
  const mobile = useIsMobile();
  const [view, setView] = useState("upload");
  const [weekKey, setWeekKey] = useState(getWeekKey());
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [weekData, setWeekData] = useState(null);
  const [history, setHistory] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [threshold, setThreshold] = useState(5);
  const [drill, setDrill] = useState(null);
  const [filterMode, setFilterMode] = useState("all");
  const [orderFile, setOrderFile] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [orderResults, setOrderResults] = useState(null);
  const [orderGuideItems, setOrderGuideItems] = useState(null);
  const [orderAnalyzing, setOrderAnalyzing] = useState(false);
  const [orderFilter, setOrderFilter] = useState("all");
  const [detectedVendors, setDetectedVendors] = useState({});
  const [analyzingStep, setAnalyzingStep] = useState("");
  const [deliveryFile, setDeliveryFile] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [deliveryResults, setDeliveryResults] = useState(null);
  const [deliveryAnalyzing, setDeliveryAnalyzing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchWithTimeout("/api/history?clientId=default", { method: "GET" }, 30000);
        const data = await r.json();
        if (data?.history) setHistory(data.history);
      } catch (e) { console.error("Load history error:", e); }
    })();
  }, []);

  const saveHist = async (h) => {
    try {
      await fetchWithTimeout("/api/history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: "default", history: h })
      }, 30000);
    } catch (e) { console.error("Save history error:", e); }
  };

  const b64 = (f) => new Promise((res,rej) => {
    const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(f);
  });

  const handleFileAdd = (files) => {
    const arr = Array.from(files);
    setUploadedFiles(prev => {
      const names = prev.map(f=>f.name);
      return [...prev, ...arr.filter(f=>!names.includes(f.name))];
    });
  };

  const removeFile = (name) => {
    setUploadedFiles(prev => prev.filter(f=>f.name!==name));
    setFileStatuses(prev => { const n={...prev}; delete n[name]; return n; });
    setFileErrors(prev => { const n={...prev}; delete n[name]; return n; });
    setDetectedVendors(prev => { const n={...prev}; delete n[name]; return n; });
  };

  const retryFile = async (file) => {
    setFileStatuses(p => ({...p, [file.name]:"loading"}));
    setFileErrors(p => { const n={...p}; delete n[file.name]; return n; });
    try {
      const result = await parsePDFAutoDetect(await b64(file), file.name);
      setDetectedVendors(p => ({...p, [file.name]: result.vendor}));
      setFileStatuses(p => ({...p, [file.name]:"done"}));
    } catch(e) {
      setFileStatuses(p => ({...p, [file.name]:"error"}));
      setFileErrors(p => ({...p, [file.name]: e.message || "Unknown error"}));
    }
  };

  const analyze = async () => {
    if (!uploadedFiles.length) return;
    setAnalyzing(true);
    const extracted = {};
    const newDetected = {};
    const newErrors = {};

    for (const file of uploadedFiles) {
      setFileStatuses(p => ({...p, [file.name]:"loading"}));
      try {
        const result = await parsePDFAutoDetect(await b64(file), file.name);
        const vendor = result.vendor;
        newDetected[file.name] = vendor;
        if (!extracted[vendor]) extracted[vendor] = [];
        extracted[vendor] = [...extracted[vendor], ...result.items];
        setFileStatuses(p => ({...p, [file.name]:"done"}));
      } catch(e) {
        setFileStatuses(p => ({...p, [file.name]:"error"}));
        newErrors[file.name] = e.message || "Unknown error";
      }
    }

    setDetectedVendors(newDetected);
    setFileErrors(newErrors);
    const activeVendors = Object.keys(extracted);
    const totals = {};
    activeVendors.forEach(v => { totals[v] = (extracted[v]||[]).reduce((s,i)=>s+(i.price||0),0); });

    // AI-powered semantic matching
    let aiMatched = [];
    if (activeVendors.length >= 2) {
      try {
        setAnalyzingStep("Matching items across vendors...");
        const matchRes = await fetchWithTimeout("/api/match-vendors", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ vendorData: extracted })
        }, 100000);
        const matchData = await matchRes.json();
        aiMatched = matchData.matched || [];
      } catch(e) {
        console.error("Match error:", e);
        setAnalyzingStep("");
      }
    }

    // Compute this week's total savings the same way the "This Week" view
    // does (best vs worst matched price per item, summed across items with
    // 2+ vendor prices), so it can be persisted for cumulative stats even
    // though the live "enriched" calc only exists while this week is open.
    const savingsSource = (aiMatched && aiMatched.length > 0) ? aiMatched : matchVendors(extracted);
    const savingsTotal = savingsSource.reduce((sum, item) => {
      const entries = Object.entries(item.vendors || {}).filter(([,v])=>v?.price!=null).map(([vendor,v])=>({vendor,price:v.price,unitPrice:v.unitPrice??null}));
      if (entries.length < 2) return sum;
      const comparedOnUnit = !!item.comparedOnUnit && entries.every(e=>e.unitPrice!=null);
      const cmpKey = comparedOnUnit ? "unitPrice" : "price";
      const best = entries.reduce((a,b)=>a[cmpKey]<b[cmpKey]?a:b);
      const worst = entries.reduce((a,b)=>a[cmpKey]>b[cmpKey]?a:b);
      return sum + (worst[cmpKey] - best[cmpKey]);
    }, 0);
    const wd = {key:weekKey, date:new Date().toISOString(), vendors:extracted, totals, activeVendors, aiMatched, savingsTotal, itemsComparedCount: savingsSource.length};
    setWeekData(wd);
    const nh = {...history, [weekKey]:wd};
    setHistory(nh); await saveHist(nh);
    setAnalyzing(false); setAnalyzingStep(""); setView("decision");
  };

  const analyzeOrderGuide = async () => {
    if (!orderFile || !weekData) return;
    setOrderAnalyzing(true); setOrderStatus("loading");
    try {
      const items = await parseOrderGuide(await b64(orderFile));
      const matched = matchOrderToVendors(items, weekData.vendors);
      setOrderResults(matched); setOrderStatus("done");
      setOrderGuideItems(items);
    } catch(e) { setOrderStatus("error: " + (e.message || "Unknown error")); }
    setOrderAnalyzing(false);
  };

  const analyzeDelivery = async () => {
    if (!deliveryFile || !orderGuideItems) return;
    setDeliveryAnalyzing(true); setDeliveryStatus("loading");
    try {
      const result = await parsePDFAutoDetect(await b64(deliveryFile), deliveryFile.name);
      const res = await fetchWithTimeout("/api/reconcile-delivery", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ orderGuideItems, deliveredItems: result.items })
      }, 100000);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "API error");
      setDeliveryResults(data); setDeliveryStatus("done");
    } catch(e) { setDeliveryStatus("error: " + (e.message || "Unknown error")); }
    setDeliveryAnalyzing(false);
  };

  const activeVendors = weekData?.activeVendors || [];
  const matched = useMemo(() => {
    if (!weekData) return [];
    if (weekData.aiMatched && weekData.aiMatched.length > 0) return weekData.aiMatched;
    return matchVendors(weekData.vendors);
  }, [weekData]);

  const enriched = useMemo(() => matched.map(item => {
    const entries = Object.entries(item.vendors).filter(([,v])=>v?.price!=null).map(([vendor,v])=>({vendor,price:v.price,unit:v.unit,unitPrice:v.unitPrice??null,unitOfMeasure:v.unitOfMeasure??null}));
    if (!entries.length) return {...item, best:null, savings:0, priceCount:0, entries:[], comparedOnUnit:false, unitOfMeasure:null};
    const comparedOnUnit = !!item.comparedOnUnit && entries.every(e=>e.unitPrice!=null);
    const cmpKey = comparedOnUnit ? "unitPrice" : "price";
    const best = entries.reduce((a,b)=>a[cmpKey]<b[cmpKey]?a:b);
    const worst = entries.reduce((a,b)=>a[cmpKey]>b[cmpKey]?a:b);
    return {...item, best, worst, savings: worst[cmpKey] - best[cmpKey], priceCount: entries.length, entries, comparedOnUnit, unitOfMeasure: comparedOnUnit ? entries[0].unitOfMeasure : null};
  }), [matched]);

  const catRollup = useMemo(() => {
    const out = {};
    CATEGORIES.forEach(cat => {
      const items = enriched.filter(i=>i.category===cat && i.priceCount>1);
      const byV = {}; activeVendors.forEach(v=>{ byV[v]=items.filter(i=>i.best?.vendor===v).length; });
      const topV = Object.entries(byV).sort((a,b)=>b[1]-a[1])[0]?.[0];
      out[cat] = {items, savings: items.reduce((s,i)=>s+i.savings,0), byV, topV};
    });
    return out;
  }, [enriched, activeVendors]);

  const splitRec = useMemo(() => {
    const r = {}; activeVendors.forEach(v=>{r[v]=[]});
    enriched.filter(i=>i.best).forEach(i=>r[i.best.vendor]?.push(i));
    return r;
  }, [enriched, activeVendors]);

  const vColor = (v) => VCOLOR[v] || "#666666";
  const vLight = (v) => VLIGHT[v] || "#F5F5F5";

  const totalSpend = useMemo(() => weekData ? Math.max(...Object.values(weekData.totals||{}).map(v=>v||0), 0) : 0, [weekData]);
  const totalSavings = useMemo(() => enriched.filter(i=>i.priceCount>1).reduce((s,i)=>s+i.savings,0), [enriched]);
  const savingsPct = totalSpend > 0 ? ((totalSavings/totalSpend)*100).toFixed(1) : "—";
  const histSorted = useMemo(() => Object.values(history).sort((a,b)=>a.key>b.key?-1:1), [history]);

  const creep = useMemo(() => {
    if (histSorted.length < 2) return [];
    const curVendors = histSorted[0].vendors || {};
    const prevVendors = histSorted[1].vendors || {};
    const results = [];
    Object.keys(curVendors).forEach(vendor => {
      const cur = curVendors[vendor] || [];
      const prev = prevVendors[vendor] || [];
      cur.forEach(ci => {
        const pi = prev.find(p => norm(p.name)===norm(ci.name));
        if (!pi) return;
        const chg = ((ci.price - pi.price)/pi.price)*100;
        if (Math.abs(chg) >= threshold) {
          results.push({vendor, name:ci.name, category:ci.category, curPrice:ci.price, prevPrice:pi.price, change:chg, unit:ci.unit});
        }
      });
    });
    return results.sort((a,b)=>b.change-a.change);
  }, [histSorted, threshold]);

  const filteredItems = useMemo(() => {
    if (filterMode==="savings") return enriched.filter(i=>i.savings>0&&i.priceCount>1);
    if (filterMode==="matched") return enriched.filter(i=>i.priceCount>1);
    return enriched;
  }, [enriched, filterMode]);

  const filteredOrder = useMemo(() => {
    if (!orderResults) return [];
    if (orderFilter==="matched") return orderResults.filter(i=>i.priceCount>0);
    if (orderFilter==="savings") return orderResults.filter(i=>i.savings>0&&i.priceCount>1);
    if (orderFilter==="unmatched") return orderResults.filter(i=>i.priceCount===0);
    return orderResults;
  }, [orderResults, orderFilter]);

  const orderTotalSavings = useMemo(() => orderResults ? orderResults.filter(i=>i.priceCount>1).reduce((s,i)=>s+i.savings,0) : 0, [orderResults]);
  const orderMatchedCount = useMemo(() => orderResults ? orderResults.filter(i=>i.priceCount>0).length : 0, [orderResults]);

  const S = {
    wrap: {minHeight:"100vh", background:"#FAF7F2", color:"#1A1410", fontFamily:"'Georgia',serif"},
    header: {background:"#1A1410", padding:mobile?"14px 16px":"20px 32px", display:"flex", flexDirection:mobile?"column":"row", alignItems:mobile?"flex-start":"center", justifyContent:"space-between", gap:12},
    nav: {background:"#2C2018", display:"flex", overflowX:"auto", WebkitOverflowScrolling:"touch"},
    navBtn: (active) => ({padding:mobile?"10px 14px":"11px 24px", border:"none", cursor:"pointer", whiteSpace:"nowrap", background:active?"#FAF7F2":"transparent", color:active?"#1A1410":"#9A8070", fontFamily:"'Georgia',serif", fontSize:mobile?13:17, borderBottom:active?"3px solid #C04A1A":"3px solid transparent"}),
    page: {padding:mobile?"16px":"32px 36px", maxWidth:1100},
    label: {fontSize:mobile?11:13, letterSpacing:2, color:"#9A8070", fontFamily:"monospace", marginBottom:6, textTransform:"uppercase"},
    card: {background:"#fff", border:"1px solid #E8E0D4", borderRadius:6, padding:mobile?"14px 16px":"20px 24px"},
    statVal: (color="#1A1410") => ({fontSize:mobile?26:40, fontWeight:"bold", color, lineHeight:1}),
    input: {background:"#fff", border:"1px solid #D0C8BE", color:"#1A1410", padding:"8px 12px", fontFamily:"monospace", fontSize:mobile?14:16, borderRadius:4, outline:"none"},
    btn: (primary) => ({background:primary?"#C04A1A":"transparent", color:primary?"#fff":"#C04A1A", border:"1.5px solid #C04A1A", padding:mobile?"10px 20px":"11px 28px", cursor:"pointer", fontFamily:"'Georgia',serif", fontSize:mobile?14:16, borderRadius:4, letterSpacing:0.5}),
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={{fontSize:mobile?10:12, letterSpacing:4, color:"#C04A1A", fontFamily:"monospace", marginBottom:3}}>SENOR JULIAN</div>
          <div style={{fontSize:mobile?18:24, color:"#FAF7F2", fontWeight:"bold"}}>Restaurant Cost Analyzer</div>
        </div>
        {weekData && totalSavings > 0 && (
          <div style={{textAlign:mobile?"left":"right"}}>
            <div style={{fontSize:10, color:"#9A8070", letterSpacing:3, fontFamily:"monospace"}}>SAVINGS ON THE TABLE</div>
            <div style={{fontSize:mobile?26:36, color:"#5DBF7A", fontWeight:"bold", lineHeight:1}}>${totalSavings.toFixed(2)}</div>
            <div style={{fontSize:12, color:"#9A8070", marginTop:2}}>{savingsPct}% of food cost</div>
          </div>
        )}
      </div>

      <div style={S.nav}>
        {[["upload","Upload"],["decision","This Week"],["orderguide","Order Guide"],["delivery","Delivery Check"],["creep","Price Creep"],["history","History"]].map(([k,l])=>(
          <button key={k} onClick={()=>setView(k)} style={S.navBtn(view===k)}>{l}</button>
        ))}
      </div>

      {/* UPLOAD TAB */}
      {view==="upload" && (
        <div style={S.page}>
          <div style={{fontSize:mobile?16:20, fontWeight:"bold", color:"#1A1410", marginBottom:6}}>Upload Price Lists</div>
          <div style={{fontSize:14, color:"#9A8070", marginBottom:20}}>Upload your vendor price lists, invoices, or quotes. The app will automatically identify each vendor and compare prices across all of them.</div>

          <div style={{display:"flex", flexWrap:"wrap", alignItems:"center", gap:12, marginBottom:20}}>
            <div style={{...S.label, marginBottom:0}}>Week of</div>
            <input type="date" defaultValue={new Date().toISOString().split("T")[0]} onChange={e=>setWeekKey(getWeekKey(new Date(e.target.value)))} style={S.input} />
          </div>

          {/* Drop zone */}
          <label style={{cursor:"pointer", display:"block", marginBottom:20}}>
            <div style={{border:"2px dashed #C04A1A88", borderRadius:10, padding:"36px 20px", textAlign:"center", background:"#FDF0EB33"}}>
              <div style={{fontSize:32, marginBottom:8}}>📂</div>
              <div style={{fontSize:16, color:"#C04A1A", fontWeight:"bold", marginBottom:4}}>Tap to add vendor files</div>
              <div style={{fontSize:13, color:"#9A8070"}}>PDF — add as many as you have. Vendor is detected automatically.</div>
            </div>
            <input type="file" accept=".pdf" multiple style={{display:"none"}} onChange={e=>handleFileAdd(e.target.files)} />
          </label>

          {/* File list */}
          {uploadedFiles.length > 0 && (
            <div style={{marginBottom:24}}>
              {uploadedFiles.map(file => {
                const st = fileStatuses[file.name];
                const detected = detectedVendors[file.name];
                const errMsg = fileErrors[file.name];
                const dColor = detected ? (VCOLOR[detected] || "#444") : "#9A8070";
                return (
                  <div key={file.name} style={{background:"#fff", border:"1px solid #E8E0D4", borderLeft:"4px solid "+(st==="done"?dColor:st==="error"?"#B02020":st==="loading"?"#A06010":"#D0C8BE"), borderRadius:6, padding:"12px 16px", marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12}}>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:14, color:"#1A1410", fontWeight:"bold", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{file.name}</div>
                      <div style={{fontSize:12, color:"#9A8070", marginTop:2, fontFamily:"monospace"}}>
                        {st==="loading" && "Reading..."}
                        {st==="done" && detected && <span style={{color:dColor}}>✓ Detected: {detected}</span>}
                        {st==="error" && <span style={{color:"#B02020"}}>{errMsg || "Error reading file"}</span>}
                        {!st && `${(file.size/1024).toFixed(0)} KB — ready`}
                      </div>
                    </div>
                    {st==="error" && !analyzing && (
                      <button onClick={()=>retryFile(file)} style={{background:"none", border:"1px solid #C04A1A", color:"#C04A1A", cursor:"pointer", fontSize:12, padding:"5px 10px", borderRadius:4, fontFamily:"monospace", whiteSpace:"nowrap"}}>Retry</button>
                    )}
                    {!analyzing && (
                      <button onClick={()=>removeFile(file.name)} style={{background:"none", border:"none", cursor:"pointer", color:"#9A8070", fontSize:20, padding:"0 4px", lineHeight:1}}>×</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button onClick={analyze} disabled={!uploadedFiles.length || analyzing} style={{...S.btn(true), opacity:!uploadedFiles.length?0.4:1, width:mobile?"100%":"auto"}}>
            {analyzing ? (analyzingStep || "Reading invoices...") : `Analyze ${uploadedFiles.length || ""} File${uploadedFiles.length!==1?"s →":"→"}`}
          </button>

          {weekData && (
            <div style={{marginTop:20, padding:"12px 16px", background:"#E8F5EE", borderRadius:6, fontSize:14, color:"#1A6640"}}>
              ✓ Last analysis: {activeVendors.join(", ")} — {enriched.filter(i=>i.priceCount>1).length} items compared
            </div>
          )}
        </div>
      )}

      {/* DECISION TAB */}
      {view==="decision" && (
        <div style={S.page}>
          {!weekData ? (
            <div style={{color:"#BEB5A8", textAlign:"center", padding:"60px 0", fontSize:16}}>No data yet. Upload price lists first.</div>
          ) : (
            <div>
              <div style={{display:"grid", gridTemplateColumns:mobile?"1fr 1fr":"repeat(4,1fr)", gap:12, marginBottom:24}}>
                {[
                  {label:"Vendors Loaded", val:activeVendors.length, color:"#1A1410"},
                  {label:"Items Compared", val:enriched.filter(i=>i.priceCount>1).length, sub:"of "+enriched.length+" total", color:"#1A1410"},
                  {label:"Savings Available", val:"$"+totalSavings.toFixed(2), color:"#1A6640"},
                  {label:"Food Cost Cut", val:savingsPct+"%", color:"#C04A1A", sub:"if buying optimal"},
                ].map(({label,val,color,sub})=>(
                  <div key={label} style={S.card}>
                    <div style={S.label}>{label}</div>
                    <div style={S.statVal(color)}>{val}</div>
                    {sub && <div style={{fontSize:12, color:"#9A8070", marginTop:4}}>{sub}</div>}
                  </div>
                ))}
              </div>

              <div style={{marginBottom:28}}>
                <div style={{...S.label, marginBottom:12}}>Split Order Recommendation</div>
                <div style={{display:"grid", gridTemplateColumns:mobile?"1fr":"repeat("+Math.min(activeVendors.length,3)+",1fr)", gap:14}}>
                  {activeVendors.map(v => {
                    const items = splitRec[v]||[];
                    return (
                      <div key={v} style={{background:"#fff", borderRadius:8, borderTop:"4px solid "+vColor(v), border:"1px solid "+vColor(v)+"44", padding:"18px"}}>
                        <div style={{fontSize:17, fontWeight:"bold", color:vColor(v), marginBottom:4}}>{v}</div>
                        <div style={{fontSize:mobile?30:38, fontWeight:"bold", color:"#1A1410", lineHeight:1}}>{items.length}</div>
                        <div style={{fontSize:14, color:"#9A8070", marginBottom:12}}>items at best price</div>
                        <div style={{borderTop:"1px solid "+vColor(v)+"22", paddingTop:10, maxHeight:160, overflowY:"auto"}}>
                          {items.slice(0,20).map(item=>(
                            <div key={item.name} style={{display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #F5F2EE", fontSize:mobile?13:15}}>
                              <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"68%", color:"#4A3C30"}}>{item.name}</span>
                              <span style={{color:vColor(v), fontFamily:"monospace", fontWeight:"bold"}}>${item.vendors[v]?.price?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{marginBottom:20}}>
                <div style={{...S.label, marginBottom:4}}>Savings by Category</div>
                <div style={{fontSize:12, color:"#9A8070", marginBottom:12}}>Items tagged <span style={{fontFamily:"monospace",color:"#1A6640"}}>⚖ per lb/ct/gal</span> are compared on true unit price. Items tagged <span style={{fontFamily:"monospace",color:"#9A8070"}}>per case</span> couldn't be normalized (no size info found) and are compared on raw case price only — those two totals aren't on the same basis, so treat this sum as directional, not exact.</div>
                <div style={{display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10}}>
                  {CATEGORIES.map(cat => {
                    const d = catRollup[cat]; const active = drill===cat;
                    return (
                      <div key={cat} onClick={()=>setDrill(active?null:cat)} style={{background:active?"#fff":"#FAF7F2", border:"1px solid "+(active?"#C04A1A":"#E8E0D4"), borderTop:"3px solid "+(d.topV?vColor(d.topV):"#E8E0D4"), borderRadius:6, padding:"12px 14px", cursor:"pointer"}}>
                        <div style={{fontSize:11, color:"#9A8070", fontFamily:"monospace", marginBottom:4}}>{cat.toUpperCase()}</div>
                        <div style={{fontSize:mobile?18:22, fontWeight:"bold", color:d.savings>0?"#C04A1A":"#D0C8BE"}}>{d.savings>0?"$"+d.savings.toFixed(2):"—"}</div>
                        <div style={{fontSize:12, color:"#9A8070", marginTop:2}}>{d.items.length} comparable</div>
                        {d.topV && <div style={{fontSize:12, color:vColor(d.topV), marginTop:3}}>{d.topV} wins {d.byV[d.topV]}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {drill && (
                <div style={{background:"#fff", border:"1px solid #E8E0D4", borderRadius:8, padding:"16px", marginBottom:20}}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
                    <div style={{fontSize:17, fontWeight:"bold", color:"#C04A1A"}}>{drill}</div>
                    <button onClick={()=>setDrill(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9A8070",fontSize:22}}>×</button>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%", borderCollapse:"collapse", fontSize:mobile?12:14, minWidth:400}}>
                      <thead><tr style={{background:"#F5F0EB"}}>
                        <th style={{textAlign:"left",padding:"8px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>ITEM</th>
                        {activeVendors.map(v=>(<th key={v} style={{textAlign:"right",padding:"8px",color:vColor(v),fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>{v}</th>))}
                        <th style={{textAlign:"right",padding:"8px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>SAVE</th>
                      </tr></thead>
                      <tbody>
                        {(catRollup[drill]?.items||[]).map((item,i)=>(
                          <tr key={item.name} style={{background:i%2===0?"#FAF7F2":"#fff"}}>
                            <td style={{padding:"8px",color:"#1A1410"}}>
                              {item.name}
                              {item.comparedOnUnit ? (
                                <span style={{marginLeft:6,fontSize:9,fontFamily:"monospace",color:"#1A6640",background:"#E8F5EE",padding:"1px 5px",borderRadius:3}}>⚖ per {item.unitOfMeasure}</span>
                              ) : (
                                <span style={{marginLeft:6,fontSize:9,fontFamily:"monospace",color:"#9A8070",background:"#F0EBE4",padding:"1px 5px",borderRadius:3}}>per case</span>
                              )}
                            </td>
                            {activeVendors.map(v=>{ const entry=item.vendors[v]; const best=item.best?.vendor===v; return (<td key={v} style={{padding:"8px",textAlign:"right",fontFamily:"monospace",fontWeight:best?"bold":"normal",color:best?vColor(v):entry?"#6A5C50":"#D0C8BE"}}>{entry?(<div><div>${entry.price.toFixed(2)}</div>{item.comparedOnUnit && entry.unitPrice!=null && (<div style={{fontSize:10,color:"#9A8070"}}>${entry.unitPrice.toFixed(2)}/{item.unitOfMeasure}</div>)}</div>):"—"}</td>); })}
                            <td style={{padding:"8px",textAlign:"right",fontFamily:"monospace",fontWeight:item.savings>0?"bold":"normal",color:item.savings>0?"#C04A1A":"#D0C8BE"}}>{item.savings>0?"$"+item.savings.toFixed(2)+(item.comparedOnUnit?"/"+item.unitOfMeasure:""):"—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{marginTop:24}}>
                <div style={{display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", marginBottom:12}}>
                  <div style={{...S.label, marginBottom:0}}>Show:</div>
                  {[["all","All"],["matched","Comparable"],["savings","Savings"]].map(([k,l])=>(<button key={k} onClick={()=>setFilterMode(k)} style={{...S.btn(filterMode===k),padding:"6px 14px",fontSize:13}}>{l}</button>))}
                </div>
                <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,overflow:"hidden"}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:mobile?12:14,minWidth:460}}>
                      <thead><tr style={{background:"#F5F0EB"}}>
                        <th style={{textAlign:"left",padding:"10px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>ITEM</th>
                        {activeVendors.map(v=>(<th key={v} style={{textAlign:"right",padding:"10px 14px",color:vColor(v),fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>{v}</th>))}
                        <th style={{textAlign:"right",padding:"10px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>SAVE</th>
                      </tr></thead>
                      <tbody>
                        {filteredItems.filter(i=>i.entries?.length).map((item,i)=>(
                          <tr key={item.name} style={{background:i%2===0?"#fff":"#FDFAF6",borderBottom:"1px solid #F0EBE4"}}>
                            <td style={{padding:"9px 14px"}}>
                              <div style={{color:"#1A1410"}}>
                                {item.name}
                                {item.comparedOnUnit ? (
                                  <span style={{marginLeft:6,fontSize:9,fontFamily:"monospace",color:"#1A6640",background:"#E8F5EE",padding:"1px 5px",borderRadius:3}}>⚖ per {item.unitOfMeasure}</span>
                                ) : (
                                  <span style={{marginLeft:6,fontSize:9,fontFamily:"monospace",color:"#9A8070",background:"#F0EBE4",padding:"1px 5px",borderRadius:3}}>per case</span>
                                )}
                              </div>
                              <div style={{fontSize:11,color:"#BEB5A8",fontFamily:"monospace"}}>{item.category}</div>
                            </td>
                            {activeVendors.map(v=>{ const e=item.vendors[v]; const best=item.best?.vendor===v&&item.priceCount>1; return (<td key={v} style={{padding:"9px 14px",textAlign:"right",fontFamily:"monospace",fontWeight:best?"bold":"normal",color:best?vColor(v):e?"#6A5C50":"#D0C8BE"}}>{e?(<div><div>${e.price.toFixed(2)}{best?" ★":""}</div>{item.comparedOnUnit && e.unitPrice!=null && (<div style={{fontSize:10,color:"#9A8070"}}>${e.unitPrice.toFixed(2)}/{item.unitOfMeasure}</div>)}</div>):"—"}</td>); })}
                            <td style={{padding:"9px 14px",textAlign:"right",fontFamily:"monospace",fontWeight:item.savings>0?"bold":"normal",color:item.savings>0?"#C04A1A":"#D0C8BE"}}>{item.savings>0?"$"+item.savings.toFixed(2)+(item.comparedOnUnit?"/"+item.unitOfMeasure:""):"—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ORDER GUIDE TAB */}
      {view==="orderguide" && (
        <div style={S.page}>
          <div style={{fontSize:mobile?16:20, fontWeight:"bold", color:"#1A1410", marginBottom:6}}>Weekly Order Guide</div>
          <div style={{fontSize:14, color:"#9A8070", marginBottom:20}}>Upload your weekly order guide. The app matches each item against your loaded vendor prices and shows you the best deal on each one.</div>
          {!weekData && <div style={{background:"#FFF8EC", border:"1px solid #E8C84A", borderRadius:6, padding:"12px 16px", marginBottom:20, fontSize:14, color:"#7A5A10"}}>Upload vendor price lists first so we have prices to compare against.</div>}
          <label style={{cursor:"pointer", display:"block", marginBottom:16, maxWidth:480}}>
            <div style={{border:"2px dashed "+(orderFile?"#C04A1A88":"#D0C8BE"), borderRadius:8, padding:"24px", textAlign:"center", background:orderFile?"#C04A1A0a":"#FAF7F2"}}>
              <div style={{fontSize:24, marginBottom:6}}>📋</div>
              {orderFile ? <div style={{fontSize:13, color:"#4A3C30", fontFamily:"monospace", fontWeight:"bold"}}>{orderFile.name}</div> : <div style={{fontSize:14, color:"#9A8070"}}>Tap to upload order guide PDF</div>}
            </div>
            <input type="file" accept=".pdf" style={{display:"none"}} onChange={e=>{ setOrderFile(e.target.files[0]); setOrderStatus(null); setOrderResults(null); }} />
          </label>
          <button onClick={analyzeOrderGuide} disabled={!orderFile||orderAnalyzing||!weekData} style={{...S.btn(true), opacity:(!orderFile||!weekData)?0.4:1, marginBottom:24}}>
            {orderAnalyzing?"Matching items...":"Find Best Prices →"}
          </button>
          {orderStatus && orderStatus.startsWith("error") && (
            <div style={{background:"#FDEEEE", border:"1px solid #E8A0A0", borderRadius:6, padding:"12px 16px", marginBottom:20, fontSize:14, color:"#B02020"}}>{orderStatus.replace(/^error:\s*/,"")}</div>
          )}

          {orderResults && (
            <div>
              <div style={{display:"grid", gridTemplateColumns:mobile?"1fr 1fr":"repeat(3,1fr)", gap:12, marginBottom:20}}>
                <div style={S.card}><div style={S.label}>Items on List</div><div style={S.statVal()}>{orderResults.length}</div></div>
                <div style={S.card}><div style={S.label}>Matched</div><div style={S.statVal("#1A6640")}>{orderMatchedCount}</div></div>
                <div style={S.card}><div style={S.label}>Savings Available</div><div style={S.statVal("#C04A1A")}>${orderTotalSavings.toFixed(2)}</div></div>
              </div>
              <div style={{display:"flex", flexWrap:"wrap", gap:8, marginBottom:14}}>
                {[["all","All"],["matched","Matched"],["savings","Has Savings"],["unmatched","Not Found"]].map(([k,l])=>(<button key={k} onClick={()=>setOrderFilter(k)} style={{...S.btn(orderFilter===k),padding:"6px 14px",fontSize:12}}>{l}</button>))}
              </div>
              <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,overflow:"hidden"}}>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:mobile?12:14,minWidth:500}}>
                    <thead><tr style={{background:"#F5F0EB"}}>
                      <th style={{textAlign:"left",padding:"10px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>ITEM</th>
                      {activeVendors.map(v=>(<th key={v} style={{textAlign:"right",padding:"10px",color:vColor(v),fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>{v}</th>))}
                      <th style={{textAlign:"right",padding:"10px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>BEST</th>
                      <th style={{textAlign:"right",padding:"10px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>SAVE</th>
                    </tr></thead>
                    <tbody>
                      {filteredOrder.map((item,i)=>(
                        <tr key={item.name+i} style={{background:i%2===0?"#fff":"#FDFAF6",borderBottom:"1px solid #F0EBE4"}}>
                          <td style={{padding:"9px 14px"}}><div style={{color:"#1A1410"}}>{item.name}</div><div style={{fontSize:11,color:"#BEB5A8",fontFamily:"monospace"}}>{item.category}</div></td>
                          {activeVendors.map(v=>{
                            const m=item.matches[v]; const isBest=item.best?.vendor===v&&item.priceCount>1;
                            return (<td key={v} style={{padding:"9px 10px",textAlign:"right",verticalAlign:"top"}}>
                              {m?<div><div style={{fontFamily:"monospace",fontWeight:isBest?"bold":"normal",color:isBest?vColor(v):"#6A5C50"}}>${m.price.toFixed(2)}{isBest?" ★":""}</div><div style={{fontSize:10,color:"#BEB5A8",fontFamily:"monospace",maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.matchedName}</div></div>:<span style={{color:"#D0C8BE",fontFamily:"monospace"}}>—</span>}
                            </td>);
                          })}
                          <td style={{padding:"9px 10px",textAlign:"right",fontFamily:"monospace",fontWeight:"bold",color:item.best?vColor(item.best.vendor):"#D0C8BE"}}>{item.best?item.best.vendor:"—"}</td>
                          <td style={{padding:"9px 10px",textAlign:"right",fontFamily:"monospace",fontWeight:item.savings>0?"bold":"normal",color:item.savings>0?"#C04A1A":"#D0C8BE"}}>{item.savings>0?"$"+item.savings.toFixed(2):"—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DELIVERY CHECK TAB */}
      {view==="delivery" && (
        <div style={S.page}>
          <div style={{fontSize:mobile?16:20, fontWeight:"bold", color:"#1A1410", marginBottom:6}}>Delivery Check</div>
          <div style={{fontSize:14, color:"#9A8070", marginBottom:20}}>Upload a delivered invoice and this checks it against your order guide — flagging anything missing, anything extra, or priced different than expected.</div>
          {!orderGuideItems && <div style={{background:"#FFF8EC", border:"1px solid #E8C84A", borderRadius:6, padding:"12px 16px", marginBottom:20, fontSize:14, color:"#7A5A10"}}>Upload and analyze your order guide first (on the Order Guide tab) so there's something to check the delivery against.</div>}
          <label style={{cursor:"pointer", display:"block", marginBottom:16, maxWidth:480}}>
            <div style={{border:"2px dashed "+(deliveryFile?"#C04A1A88":"#D0C8BE"), borderRadius:8, padding:"24px", textAlign:"center", background:deliveryFile?"#C04A1A0a":"#FAF7F2"}}>
              <div style={{fontSize:24, marginBottom:6}}>🚚</div>
              {deliveryFile ? <div style={{fontSize:13, color:"#4A3C30", fontFamily:"monospace", fontWeight:"bold"}}>{deliveryFile.name}</div> : <div style={{fontSize:14, color:"#9A8070"}}>Tap to upload delivered invoice PDF</div>}
            </div>
            <input type="file" accept=".pdf" style={{display:"none"}} onChange={e=>{ setDeliveryFile(e.target.files[0]); setDeliveryStatus(null); setDeliveryResults(null); }} />
          </label>
          <button onClick={analyzeDelivery} disabled={!deliveryFile||deliveryAnalyzing||!orderGuideItems} style={{...S.btn(true), opacity:(!deliveryFile||!orderGuideItems)?0.4:1, marginBottom:24}}>
            {deliveryAnalyzing?"Checking delivery...":"Check Delivery →"}
          </button>
          {deliveryStatus && deliveryStatus.startsWith("error") && (
            <div style={{background:"#FDEEEE", border:"1px solid #E8A0A0", borderRadius:6, padding:"12px 16px", marginBottom:20, fontSize:14, color:"#B02020"}}>{deliveryStatus.replace(/^error:\s*/,"")}</div>
          )}

          {deliveryResults && (
            <div>
              <div style={{display:"grid", gridTemplateColumns:mobile?"1fr 1fr":"repeat(3,1fr)", gap:12, marginBottom:24}}>
                <div style={S.card}><div style={S.label}>Matched OK</div><div style={S.statVal("#1A6640")}>{deliveryResults.matchedCount}</div></div>
                <div style={S.card}><div style={S.label}>Missing</div><div style={S.statVal(deliveryResults.missing.length>0?"#C04A1A":"#1A6640")}>{deliveryResults.missing.length}</div></div>
                <div style={S.card}><div style={S.label}>Not Ordered</div><div style={S.statVal(deliveryResults.extra.length>0?"#C04A1A":"#1A6640")}>{deliveryResults.extra.length}</div></div>
              </div>

              {deliveryResults.priceMismatches.length > 0 && (
                <div style={{marginBottom:24}}>
                  <div style={{...S.label, marginBottom:10}}>Price Mismatches</div>
                  <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,overflow:"hidden"}}>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:mobile?12:14,minWidth:400}}>
                        <thead><tr style={{background:"#F5F0EB"}}>
                          <th style={{textAlign:"left",padding:"9px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>ITEM</th>
                          <th style={{textAlign:"right",padding:"9px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>EXPECTED</th>
                          <th style={{textAlign:"right",padding:"9px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>BILLED</th>
                          <th style={{textAlign:"right",padding:"9px 14px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>DIFF</th>
                        </tr></thead>
                        <tbody>
                          {deliveryResults.priceMismatches.map((item,i)=>(
                            <tr key={item.name+i} style={{background:i%2===0?"#fff":"#FDFAF6",borderBottom:"1px solid #F0EBE4",borderLeft:"3px solid "+(item.diff>0?"#C04A1A":"#1A6640")}}>
                              <td style={{padding:"9px 14px",color:"#1A1410"}}>{item.name}</td>
                              <td style={{padding:"9px 14px",textAlign:"right",fontFamily:"monospace",color:"#9A8070"}}>${item.expectedPrice?.toFixed(2)}</td>
                              <td style={{padding:"9px 14px",textAlign:"right",fontFamily:"monospace",fontWeight:"bold",color:"#1A1410"}}>${item.actualPrice?.toFixed(2)}</td>
                              <td style={{padding:"9px 14px",textAlign:"right",fontFamily:"monospace",fontWeight:"bold",color:item.diff>0?"#C04A1A":"#1A6640"}}>{item.diff>0?"+":""}${item.diff?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {deliveryResults.missing.length > 0 && (
                <div style={{marginBottom:24}}>
                  <div style={{...S.label, marginBottom:10}}>On Order Guide, Not On This Delivery</div>
                  <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,overflow:"hidden"}}>
                    {deliveryResults.missing.map((item,i)=>(
                      <div key={item.name+i} style={{display:"flex",justifyContent:"space-between",padding:"9px 14px",borderBottom:i<deliveryResults.missing.length-1?"1px solid #F0EBE4":"none",background:i%2===0?"#fff":"#FDFAF6"}}>
                        <span style={{color:"#1A1410",fontSize:mobile?13:14}}>{item.name}</span>
                        <span style={{color:"#9A8070",fontFamily:"monospace",fontSize:mobile?13:14}}>expected ${item.expectedPrice?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {deliveryResults.extra.length > 0 && (
                <div style={{marginBottom:24}}>
                  <div style={{...S.label, marginBottom:10}}>On This Delivery, Not On Order Guide</div>
                  <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,overflow:"hidden"}}>
                    {deliveryResults.extra.map((item,i)=>(
                      <div key={item.name+i} style={{display:"flex",justifyContent:"space-between",padding:"9px 14px",borderBottom:i<deliveryResults.extra.length-1?"1px solid #F0EBE4":"none",background:i%2===0?"#fff":"#FDFAF6"}}>
                        <span style={{color:"#1A1410",fontSize:mobile?13:14}}>{item.name}</span>
                        <span style={{color:"#9A8070",fontFamily:"monospace",fontSize:mobile?13:14}}>${item.price?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {deliveryResults.missing.length===0 && deliveryResults.extra.length===0 && deliveryResults.priceMismatches.length===0 && (
                <div style={{...S.card,textAlign:"center",padding:"28px",color:"#1A6640",fontSize:15}}>Everything on this delivery matches the order guide — no missing items, no substitutions, no price differences.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PRICE CREEP TAB */}
      {view==="creep" && (
        <div style={S.page}>
          <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:12,marginBottom:24}}>
            <div style={{...S.label,marginBottom:0}}>Flag PFG items that moved more than</div>
            <input type="number" value={threshold} min={1} max={50} onChange={e=>setThreshold(parseInt(e.target.value)||5)} style={{...S.input,width:64,textAlign:"center"}} />
            <div style={{...S.label,marginBottom:0}}>% week over week</div>
          </div>
          {histSorted.length < 2 ? (
            <div style={{...S.card,textAlign:"center",padding:"40px 20px"}}><div style={{fontSize:16,color:"#9A8070"}}>Need at least 2 weeks of data.</div></div>
          ) : (
            <div>
              <div style={{display:"grid",gridTemplateColumns:mobile?"1fr 1fr":"repeat(3,1fr)",gap:12,marginBottom:24}}>
                <div style={S.card}><div style={S.label}>Items Flagged</div><div style={S.statVal(creep.length>0?"#C04A1A":"#1A6640")}>{creep.length}</div></div>
                <div style={S.card}><div style={S.label}>Biggest Jump</div><div style={S.statVal("#C04A1A")}>{creep[0]?"+"+creep[0].change.toFixed(1)+"%":"—"}</div><div style={{fontSize:12,color:"#9A8070",marginTop:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{creep[0]?.name||"none"}</div></div>
                <div style={{...S.card,gridColumn:mobile?"1 / -1":"auto"}}><div style={S.label}>Comparing</div><div style={{fontSize:15,fontWeight:"bold",color:"#1A1410",marginTop:4}}>{weekLabel(histSorted[1]?.key)}</div><div style={{fontSize:13,color:"#9A8070",marginTop:2}}>to {weekLabel(histSorted[0]?.key)}</div></div>
              </div>
              {creep.length===0 ? (
                <div style={{...S.card,textAlign:"center",padding:"28px",color:"#1A6640",fontSize:15}}>No items moved more than {threshold}% this week.</div>
              ) : (
                <div style={{background:"#fff",border:"1px solid #E8E0D4",borderRadius:8,overflow:"hidden"}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:mobile?12:14,minWidth:460}}>
                      <thead><tr style={{background:"#F5F0EB"}}>
                        {["Item","Vendor","Category","Previous","This Week","Change"].map(h=>(<th key={h} style={{textAlign:h==="Item"||h==="Vendor"||h==="Category"?"left":"right",padding:"10px 12px",color:"#9A8070",fontWeight:"normal",fontFamily:"monospace",fontSize:10}}>{h.toUpperCase()}</th>))}
                      </tr></thead>
                      <tbody>
                        {creep.map((item,i)=>(
                          <tr key={item.vendor+item.name} style={{background:i%2===0?"#fff":"#FDFAF6",borderBottom:"1px solid #F0EBE4",borderLeft:"3px solid "+(item.change>0?"#C04A1A":"#1A6640")}}>
                            <td style={{padding:"10px 12px",color:"#1A1410"}}>{item.name}</td>
                            <td style={{padding:"10px 12px",color:"#9A8070",fontSize:12}}>{item.vendor}</td>
                            <td style={{padding:"10px 12px",color:"#9A8070",fontSize:12}}>{item.category}</td>
                            <td style={{padding:"10px 12px",textAlign:"right",fontFamily:"monospace",color:"#9A8070"}}>${item.prevPrice.toFixed(2)}</td>
                            <td style={{padding:"10px 12px",textAlign:"right",fontFamily:"monospace",fontWeight:"bold",color:"#1A1410"}}>${item.curPrice.toFixed(2)}</td>
                            <td style={{padding:"10px 12px",textAlign:"right",fontFamily:"monospace",fontWeight:"bold",color:item.change>0?"#C04A1A":"#1A6640"}}>{item.change>0?"+":""}{item.change.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* HISTORY TAB */}
      {view==="history" && (
        <div style={S.page}>
          {Object.keys(history).length===0 ? (
            <div style={{...S.card,textAlign:"center",padding:"40px 20px"}}><div style={{fontSize:16,color:"#9A8070"}}>No history yet.</div></div>
          ) : (
            <div>
              <div style={{...S.label,marginBottom:14}}>Invoice History</div>
              {histSorted.map(week=>(
                <div key={week.key} style={{...S.card,marginBottom:12}}>
                  <div style={{display:"flex",flexWrap:"wrap",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:10}}>
                    <div style={{fontSize:17,fontWeight:"bold",color:"#1A1410"}}>Week of {weekLabel(week.key)}</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {(week.activeVendors||Object.keys(week.vendors||{})).filter(v=>week.vendors[v]).map(v=>(<span key={v} style={{fontSize:11,fontFamily:"monospace",padding:"3px 8px",borderRadius:3,background:vLight(v),color:vColor(v)}}>{v} · {week.vendors[v].length} items</span>))}
                    </div>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:20}}>
                    {(week.activeVendors||Object.keys(week.totals||{})).filter(v=>week.totals[v]).map(v=>(<div key={v}><div style={{fontSize:11,color:"#9A8070",fontFamily:"monospace"}}>{v}</div><div style={{fontSize:18,fontFamily:"monospace",fontWeight:"bold",color:vColor(v)}}>${week.totals[v].toFixed(2)}</div></div>))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
